import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import { fetchCountriesIfNeeded, State as CountryState } from '~/Country';
import { handleError } from '~/ErrorHandler';
import { ThunkDispatch } from '~/Store';
import Autocomplete, { Option } from '~/UI/Autocomplete/Autocomplete';

interface OwnProps {
	value?: string;
	selectedIds?: string[] | string;
}

const mapOptions = memoizeOne((countries: Country[]): Option[] => {
	return countries.map((country) => ({
		id: country.alpha2,
		title: country.name,
	}));
});

const filterOptions = memoizeOne((options: Option[], query: string): Option[] => {
	const lowerCasedQuery = query.toLowerCase();
	const filteredOptions = options.filter((option) => option.title.toLowerCase().includes(lowerCasedQuery));
	filteredOptions.sort((a, b) => a.title.localeCompare(b.title));
	return filteredOptions;
});

const mapSelectedOptions = memoizeOne((options: Option[], selectedIds: string[]): Option[] => {
	const selectedOptions: any = selectedIds
		.map((id) => options.find((option) => option.id === id))
		.filter((option) => Boolean(option));
	return selectedOptions;
});

function mapStateToProps(
	{ country }: { country: CountryState },
	{ value, selectedIds }: OwnProps
): {
	options: Option[];
	selectedOptions: Option[];
} {
	const options = mapOptions(country.countries);
	const truthySelectedIds = selectedIds || [];
	const normalizedSelectedIds = Array.isArray(truthySelectedIds) ? truthySelectedIds : [truthySelectedIds];

	return {
		options: filterOptions(options, value || ''),
		selectedOptions: mapSelectedOptions(options, normalizedSelectedIds),
	};
}

function mapDispatchToProps(dispatch: ThunkDispatch<AnyAction>): {
	onFetch(): void;
} {
	return {
		onFetch: () => dispatch(fetchCountriesIfNeeded()).catch((response) => dispatch(handleError(response))),
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(Autocomplete);
